import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  TextField,
  Tooltip,
  Divider,
  Stack,
  Alert,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useCalculator } from "../../contexts/CalculatorContext";
import { formatCurrency } from "../../utils/formatters";
import { useTranslation } from "react-i18next";

/**
 * ProductCalculator component displays a list of added variable products
 * and allows adding them to cart in bulk
 * @param {Object} props - Component props
 * @returns {JSX.Element} ProductCalculator component
 */
const ProductCalculator = ({ sx = {} }) => {
  const { t } = useTranslation("common");
  const { t: tProducts } = useTranslation("products");
  const {
    calculatorItems,
    loading,
    error,
    updateCalculatorItem,
    removeFromCalculator,
    clearCalculator,
    addAllToCart,
    subtotal,
    checkAvailableStock,
  } = useCalculator();

  // Handle quantity change
  const handleQuantityChange = (variationId, event) => {
    const newQuantity = parseInt(event.target.value);
    if (!isNaN(newQuantity) && newQuantity > 0) {
      updateCalculatorItem(variationId, newQuantity);
    }
  };

  // Handle increment quantity
  const handleIncrement = (item) => {
    // Check if incrementing would exceed available stock
    const newQuantity = item.quantity + 1;

    // Use the checkAvailableStock function to validate
    const stockCheck = checkAvailableStock(
      item.variation,
      newQuantity,
      item.variation.id
    );

    if (!stockCheck.isExceeded) {
      updateCalculatorItem(item.variation.id, newQuantity);
    } else {
      // The updateCalculatorItem function will handle the warning
      updateCalculatorItem(item.variation.id, stockCheck.allowedQuantity);
    }
  };

  // Handle decrement quantity
  const handleDecrement = (item) => {
    if (item.quantity > 1) {
      const newQuantity = item.quantity - 1;
      updateCalculatorItem(item.variation.id, newQuantity);
    }
  };

  // Handle remove item
  const handleRemoveItem = (variationId) => {
    removeFromCalculator(variationId);
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        mb: 3,
        ...sx,
      }}
    >
      <Typography variant="h6" gutterBottom>
        {tProducts("calculator.title")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {calculatorItems.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {tProducts("calculator.emptyMessage")}
        </Alert>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    {tProducts("calculator.columns.variation")}
                  </TableCell>
                  <TableCell align="right">
                    {tProducts("calculator.columns.price")}
                  </TableCell>
                  <TableCell align="center">
                    {tProducts("calculator.columns.quantity")}
                  </TableCell>
                  <TableCell align="right">
                    {tProducts("calculator.columns.total")}
                  </TableCell>
                  <TableCell align="center">
                    {tProducts("calculator.columns.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {calculatorItems.map((item) => {
                  // Extract variation name for display
                  let displayName = item.variation.name || "";
                  if (displayName.startsWith(item.product.name)) {
                    displayName = displayName
                      .substring(item.product.name.length)
                      .trim();
                    if (displayName.startsWith("-")) {
                      displayName = displayName.substring(1).trim();
                    }
                  }
                  if (!displayName) {
                    displayName = `Variation ${item.variation.id.substring(
                      0,
                      8
                    )}`;
                  }

                  // Check available stock for this item
                  const stockCheck = checkAvailableStock(
                    item.variation,
                    item.quantity,
                    item.variation.id
                  );

                  // Determine if at max stock
                  const isAtMaxStock =
                    item.quantity >=
                    stockCheck.totalStock - stockCheck.cartQuantity;

                  return (
                    <TableRow key={item.variation.id}>
                      <TableCell>
                        {displayName}
                        {stockCheck.cartQuantity > 0 && (
                          <Chip
                            size="small"
                            label={tProducts("calculator.inCartCount", {
                              count: stockCheck.cartQuantity,
                            })}
                            color="primary"
                            variant="outlined"
                            sx={{ ml: 1, fontSize: "0.7rem" }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(parseFloat(item.variation.price))}
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Tooltip
                            title={tProducts("shared.quantity.decrement")}
                          >
                            <IconButton
                              size="small"
                              onClick={() => handleDecrement(item)}
                              disabled={item.quantity <= 1}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <TextField
                            size="small"
                            value={item.quantity}
                            onChange={(e) =>
                              handleQuantityChange(item.variation.id, e)
                            }
                            inputProps={{
                              min: 1,
                              style: {
                                textAlign: "center",
                                width: "40px",
                                padding: "4px",
                              },
                            }}
                            variant="standard"
                            sx={{ mx: 1, width: "50px" }}
                          />

                          <Tooltip
                            title={
                              isAtMaxStock
                                ? tProducts("shared.status.outOfStock")
                                : tProducts("shared.quantity.increment")
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleIncrement(item)}
                                disabled={isAtMaxStock}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(
                          parseFloat(item.variation.price) * item.quantity
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={tProducts("calculator.remove")}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveItem(item.variation.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Typography variant="subtitle1">
              {tProducts("calculator.total")}: {formatCurrency(subtotal)}
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ mt: { xs: 2, sm: 0 } }}
            >
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={clearCalculator}
              >
                {tProducts("calculator.clearAll")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<ShoppingCartIcon />}
                onClick={addAllToCart}
                disabled={loading || calculatorItems.length === 0}
              >
                {tProducts("calculator.addAllToCart")}
              </Button>
            </Stack>
          </Box>
        </>
      )}
    </Paper>
  );
};

ProductCalculator.propTypes = {
  /** Additional styles */
  sx: PropTypes.object,
};

export default ProductCalculator;
